import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Snackbar,
  Alert,
  AppBar,
  Toolbar,
  Container,
  Paper,
  Tooltip,
  Divider
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, serverTimestamp, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import TopBar from './TopBar';
import OcrUploader from './OcrUploader';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { FormatBold, FormatClear } from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CropModal from './CropModal';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormattedTextEditor from './FormattedTextEditor';
import { getPlainText } from '../utils/textFormatting';

function HighlightCreatePage() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  console.log('[HighlightCreatePage] Initial Render - bookId from params:', bookId);
  console.log('[HighlightCreatePage] Initial Render - location.state:', location.state);

  const initialBookInfo = location.state?.book || null;

  const [bookInfo, setBookInfo] = useState(initialBookInfo);
  const [highlightText, setHighlightText] = useState('');
  const [formattedHighlightText, setFormattedHighlightText] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingBookInfo, setLoadingBookInfo] = useState(!initialBookInfo);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [ocrMode, setOcrMode] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState('');
  const [inputStarted, setInputStarted] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [croppedImage, setCroppedImage] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(null);
  const [remainingImages, setRemainingImages] = useState(0);
  const MAX_IMAGES = 30; // 최대 이미지 개수 제한
  const [scanConfirmOpen, setScanConfirmOpen] = useState(false);

  const fileInputRef = useRef(null);
  const textFieldRef = useRef(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 모바일 키보드 높이 감지 (visualViewport API - 하단 액션 바 위치용)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const handleViewportResize = () => {
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const wh = window.innerHeight;
      setKeyboardHeight(Math.max(0, wh - vh));
    };
    handleViewportResize();
    window.visualViewport.addEventListener('resize', handleViewportResize);
    window.visualViewport.addEventListener('scroll', handleViewportResize);
    return () => {
      window.visualViewport.removeEventListener('resize', handleViewportResize);
      window.visualViewport.removeEventListener('scroll', handleViewportResize);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    console.log('[HighlightCreatePage] useEffect triggered - bookId:', bookId, 'bookInfo exists:', !!bookInfo);

    const fetchBookInfo = async () => {
      if (!bookInfo && bookId) {
        console.log(`[HighlightCreatePage] Fetching book from Firestore for ID: ${bookId}`);
        setLoadingBookInfo(true);
        setError('');
        try {
          const bookRef = doc(db, 'books', bookId);
          const bookSnap = await getDoc(bookRef);

          if (bookSnap.exists()) {
            const fetchedData = { id: bookSnap.id, ...bookSnap.data() };
            console.log("[HighlightCreatePage] Book info fetched:", fetchedData);
            setBookInfo(fetchedData);
            setError('');
          } else {
            console.error(`[HighlightCreatePage] Firestore Error: Book document with ID ${bookId} not found.`);
            setError(`책 정보를 찾을 수 없습니다 (ID: ${bookId}). Firestore 문서를 확인해주세요.`);
            setBookInfo(null);
          }
        } catch (err) {
          console.error('[HighlightCreatePage] Firestore fetch error:', err);
          setError(`책 정보 로딩 중 오류 발생: ${err.message}`);
          setBookInfo(null);
        } finally {
          console.log('[HighlightCreatePage] Finished fetching, setting loading to false.');
          setLoadingBookInfo(false);
        }
      } else if (bookInfo) {
        console.log('[HighlightCreatePage] Using bookInfo from location state.');
        if (loadingBookInfo) setLoadingBookInfo(false);
      } else if (!bookId) {
        console.error("[HighlightCreatePage] Error: bookId is missing.");
        setError('잘못된 접근입니다. URL에 책 ID가 없습니다.');
        setLoadingBookInfo(false);
      }
    };

    fetchBookInfo();
  }, [bookId]);

  const handleSaveHighlight = async () => {
    const textToSave = formattedHighlightText ? getPlainText(formattedHighlightText) : highlightText;
    if (!textToSave.trim()) {
      setSnackbar({ open: true, message: '하이라이트 내용을 입력해주세요.', severity: 'warning' });
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      setSnackbar({ open: true, message: '로그인이 필요합니다.', severity: 'error' });
      return;
    }
    if (!bookInfo || !bookInfo.isbn) {
      console.error("HighlightCreatePage: Cannot save highlight, book info or ISBN is missing.", bookInfo);
      setSnackbar({ open: true, message: '책 정보(ISBN)가 유효하지 않아 저장할 수 없습니다.', severity: 'error' });
      return;
    }

    setIsSaving(true);
    setError('');

    // ✅ bookId (ISBN) 전처리: 공백이 있으면 첫 부분만 사용
    const cleanIsbn = (bookInfo.isbn || '').split(' ')[0];

    const highlightData = {
      bookId: cleanIsbn, // 전처리된 ISBN 사용
      bookTitle: bookInfo.title || '제목 없음',
      bookAuthors: bookInfo.authors || [],
      bookThumbnail: bookInfo.thumbnail || '',
      text: textToSave.trim(),
      formattedText: formattedHighlightText, // 포맷팅된 텍스트 저장
      searchableText: textToSave.trim().toLowerCase(),
      uid: user.uid,
      userName: user.displayName || '익명',
      userPhotoURL: user.photoURL || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      // 1. 하이라이트 저장
      const highlightDocRef = await addDoc(collection(db, 'highlights'), highlightData);
      console.log("Highlight saved with ID: ", highlightDocRef.id);

      // 2. 책 문서 존재 확인 및 자동 생성
      const firestoreDocId = `${user.uid}_${cleanIsbn}`; 
      const bookRef = doc(db, 'books', firestoreDocId); 
      const bookSnap = await getDoc(bookRef); // Check if book exists

      if (!bookSnap.exists()) {
        console.log(`[HighlightCreatePage] Book document ${firestoreDocId} not found. Creating...`);
        const bookDataToSave = {
          title: bookInfo.title || '제목 없음',
          authors: bookInfo.authors || [],
          thumbnail: bookInfo.thumbnail || '',
          isbn: cleanIsbn, // 전처리된 ISBN 사용
          uid: user.uid, // 사용자 UID 저장
          createdAt: serverTimestamp(), // 추가된 시간 저장
          // HighlightCreatePage는 publisher, contents, datetime 정보가 없으므로, 
          // 여기서 생성 시 이 필드들은 포함되지 않음. 필요시 BookDetail 등에서 업데이트.
        };
        await setDoc(bookRef, bookDataToSave); // setDoc으로 특정 ID 문서 생성
        console.log(`[HighlightCreatePage] Book document ${firestoreDocId} created successfully.`);
      } else {
        console.log(`[HighlightCreatePage] Book document ${firestoreDocId} already exists.`);
      }

      // 저장 성공 후 다음 이미지로 넘어가기
      if (remainingImages > 0) {
        handleNextImage();
      } else {
        // 모든 이미지 처리 완료 후 리디렉션
        if (user && firestoreDocId) {
          navigate(`/book/${firestoreDocId}`, { replace: true });
        } else {
          navigate('/mypage', { replace: true });
        }
      }

    } catch (err) {
      console.error('Highlight save error:', err);
      setError('하이라이트 저장 중 오류가 발생했습니다.');
      setSnackbar({ open: true, message: `하이라이트 저장 오류: ${err.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  // OcrUploader의 리사이즈+OCR 로직을 직접 사용
  const resizeImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new window.Image();
        img.src = event.target.result;
        img.onload = () => {
          let width = img.width;
          let height = img.height;
          const MAX_SIZE = 800;
          if (width > MAX_SIZE || height > MAX_SIZE) {
            if (width > height) {
              height = Math.round((height * MAX_SIZE) / width);
              width = MAX_SIZE;
            } else {
              width = Math.round((width * MAX_SIZE) / height);
              height = MAX_SIZE;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          const resizedBase64 = canvas.toDataURL('image/jpeg', 0.8);
          resolve(resizedBase64);
        };
      };
    });
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // 최대 이미지 개수 제한 체크
    if (files.length > MAX_IMAGES) {
      setSnackbar({ 
        open: true, 
        message: `이미지는 최대 ${MAX_IMAGES}개까지만 선택할 수 있습니다. ${files.length}개 중 ${MAX_IMAGES}개만 처리됩니다.`, 
        severity: 'warning',
        autoHideDuration: 4000
      });
      // MAX_IMAGES 개수만큼만 처리
      const limitedFiles = files.slice(0, MAX_IMAGES);
      processSelectedFiles(limitedFiles);
      return;
    }

    processSelectedFiles(files);
  };

  const processSelectedFiles = (files) => {
    // 파일 유효성 검사
    const validFiles = files.filter(file => {
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        setSnackbar({ 
          open: true, 
          message: 'JPG 또는 PNG 이미지만 업로드 가능합니다.', 
          severity: 'error',
          autoHideDuration: 3000
        });
        return false;
      }
      if (file.size > 4 * 1024 * 1024) {
        setSnackbar({ 
          open: true, 
          message: '이미지 크기는 4MB 이하여야 합니다.', 
          severity: 'error',
          autoHideDuration: 3000
        });
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setUploadedImages(validFiles);
      setCurrentImageIndex(0);
      setRemainingImages(validFiles.length);
      setSelectedImage(validFiles[0]);
      setShowCropper(true);
      
      // 선택된 이미지 개수 안내
      if (validFiles.length < files.length) {
        setSnackbar({
          open: true,
          message: `${files.length}개 중 ${validFiles.length}개의 유효한 이미지가 선택되었습니다.`,
          severity: 'info',
          autoHideDuration: 3000
        });
      }
    }
  };

  const handleCropComplete = async (croppedBlob) => {
    try {
      setShowCropper(false);
      setOcrLoading(true);
      setOcrError('');

      const croppedFile = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
      const imageData = await resizeImage(croppedFile);
      const imageBase64 = imageData.split(',')[1];
      
      const response = await fetch('https://asia-northeast3-booking-6ec89.cloudfunctions.net/extractText', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64 })
      });

      if (!response.ok) {
        throw new Error('OCR 서버 응답 오류');
      }

      const data = await response.json();
      if (data && data.text && data.text.trim()) {
        const processedText = data.text.replace(/\n/g, '');
        setHighlightText(processedText);
        setFormattedHighlightText(null); // OCR 결과는 포맷팅 없이 저장
        setInputStarted(true);
        setOcrError('');
        setTimeout(() => {
          // 포커스는 FormattedTextEditor 내부에서 처리됨
        }, 100);
      } else {
        throw new Error('텍스트를 추출할 수 없습니다.');
      }
    } catch (err) {
      console.error('OCR 처리 오류:', err);
      setSnackbar({ 
        open: true, 
        message: err.message === '텍스트를 추출할 수 없습니다.' 
          ? '이미지에서 텍스트를 추출하지 못했습니다. 다시 시도해주세요.' 
          : 'OCR 처리 중 오류가 발생했습니다.',
        severity: 'error' 
      });
    } finally {
      setOcrLoading(false);
      setSelectedImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleNextImage = () => {
    if (currentImageIndex < uploadedImages.length - 1) {
      const nextIndex = currentImageIndex + 1;
      setCurrentImageIndex(nextIndex);
      setSelectedImage(uploadedImages[nextIndex]);
      setShowCropper(true);
      setRemainingImages(uploadedImages.length - nextIndex - 1);
    } else {
      // 모든 이미지 처리 완료
      setUploadedImages([]);
      setCurrentImageIndex(null);
      setRemainingImages(0);
      setSnackbar({ 
        open: true, 
        message: '모든 이미지 처리가 완료되었습니다.', 
        severity: 'success' 
      });
    }
  };

  const handleCancelCurrentOrAll = () => {
    // 복수 이미지 등록 중 & 현재 이미지가 남아있으면: 현재 이미지만 건너뛰고 다음 이미지로 이동
    if (uploadedImages.length > 0 && currentImageIndex !== null && uploadedImages.length > 1) {
      // 마지막 이미지라면 전체 종료
      if (currentImageIndex === uploadedImages.length - 1) {
        setUploadedImages([]);
        setCurrentImageIndex(null);
        setRemainingImages(0);
        setSnackbar({ open: true, message: '모든 이미지 처리가 완료되었습니다.', severity: 'success' });
        navigate(-1);
      } else {
        setSnackbar({ open: true, message: '이 이미지는 건너뛰었습니다.', severity: 'info' });
        handleNextImage();
      }
    } else {
      // 단일 이미지/입력만 있을 때는 전체 취소
      navigate(-1);
    }
  };

  // 진행상황 계산
  const totalImages = uploadedImages.length || 0;
  const currentNum = currentImageIndex !== null ? currentImageIndex + 1 : 0;

  // 버튼 텍스트
  const scanButtonText = totalImages === 0
    ? `텍스트 스캔하기 (0/${MAX_IMAGES})`
    : `${currentNum}/${totalImages} 이미지 등록 중`;

  // 파일 선택창 열기 함수 분리
  const openFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // 스캔 버튼 클릭 핸들러 개선
  const handleScanButtonClick = () => {
    if (uploadedImages.length > 0) {
      setScanConfirmOpen(true);
    } else {
      openFileInput();
    }
  };

  // 안내 모달에서 Y(계속) 선택 시
  const handleScanConfirmYes = () => {
    setScanConfirmOpen(false);
    // 기존 이미지/입력 내역 초기화
    setUploadedImages([]);
    setCurrentImageIndex(null);
    setRemainingImages(0);
    setSelectedImage(null);
    setHighlightText('');
    setFormattedHighlightText(null);
    setTimeout(() => {
      openFileInput();
    }, 100); // 상태 초기화 후 파일 선택창 열기
  };

  // 안내 모달에서 N(취소) 선택 시
  const handleScanConfirmNo = () => {
    setScanConfirmOpen(false);
  };

  if (loadingBookInfo) {
    console.log('[HighlightCreatePage] Rendering: Loading State');
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        {/* TopBar 제거 */}
        <AppBar
          position="sticky"
          color="inherit"
          elevation={2}
          sx={{
            top: 0,
            zIndex: 1201,
            borderBottom: '1px solid #eee',
            bgcolor: 'background.paper',
          }}
        >
          <Toolbar sx={{ px: 2, minHeight: 56, display: 'flex', justifyContent: 'space-between' }}>
            {/* 스캔/취소/저장 버튼 그룹 (로딩 중엔 비워둠) */}
          </Toolbar>
        </AppBar>
        <Container sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', pt: { xs: 10, sm: 11 } }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>책 정보를 불러오는 중...</Typography>
        </Container>
      </Box>
    );
  }

  if (error) {
    console.log('[HighlightCreatePage] Rendering: Error State -', error);
     return (
       <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
         {/* TopBar 제거 */}
         <AppBar
           position="sticky"
           color="inherit"
           elevation={2}
           sx={{
             top: 0,
             zIndex: 1201,
             borderBottom: '1px solid #eee',
             bgcolor: 'background.paper',
           }}
         >
           <Toolbar sx={{ px: 2, minHeight: 56, display: 'flex', justifyContent: 'space-between' }}>
             {/* 스캔/취소/저장 버튼 그룹 (에러 시엔 비워둠) */}
           </Toolbar>
         </AppBar>
         <Container sx={{ flexGrow: 1, textAlign: 'center', pt: { xs: 10, sm: 11 } }}>
           <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
           <Button onClick={handleCancelCurrentOrAll} variant="outlined">뒤로가기</Button>
         </Container>
       </Box>
     );
  }

  if (!bookInfo) {
      console.log('[HighlightCreatePage] Rendering: No Book Info State (after loading & no error)');
       return (
         <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
           {/* TopBar 제거 */}
            <AppBar
              position="sticky"
              color="inherit"
              elevation={2}
              sx={{
                top: 0,
                zIndex: 1201,
                borderBottom: '1px solid #eee',
                bgcolor: 'background.paper',
              }}
            >
              <Toolbar sx={{ px: 2, minHeight: 56, display: 'flex', justifyContent: 'space-between' }}>
                {/* 스캔/취소/저장 버튼 그룹 (정보 없음 시엔 비워둠) */}
              </Toolbar>
            </AppBar>
            <Container sx={{ flexGrow: 1, textAlign: 'center', pt: { xs: 10, sm: 11 } }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                책 정보를 표시할 수 없습니다. 이전 페이지로 돌아가 다시 시도해주세요. (bookId: {bookId || '없음'})
              </Typography>
              <Button onClick={handleCancelCurrentOrAll} variant="outlined">뒤로가기</Button>
            </Container>
         </Box>
       );
    }

  console.log('[HighlightCreatePage] Rendering: Success State with bookInfo:', bookInfo);

  const ACTION_BAR_HEIGHT = 56;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#fff' }}>
      {/* 메인 콘텐츠 영역 - 상단 safe-area, 하단 액션 바 높이만큼 패딩 */}
      <Box 
        sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          overflow: 'hidden',
          bgcolor: '#ffffff',
          pt: 'env(safe-area-inset-top, 0px)',
          pb: `calc(${ACTION_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
          position: 'relative'
        }}
      >

        {/* OCR 업로드/로딩/실패 */}
        {ocrMode && (
          <Box sx={{ 
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            pb: 2
          }}>
            <OcrUploader />
            {ocrLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>텍스트 추출 중...</Typography>
              </Box>
            )}
            {ocrError && (
              <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>{ocrError}</Typography>
            )}
            <Button
              fullWidth
              variant="outlined"
              size="large"
              sx={{ mt: 2, py: 1.5, fontWeight: 500 }}
              onClick={() => setOcrMode(false)}
            >
              직접 입력으로 돌아가기
            </Button>
          </Box>
        )}

        {/* 메인 입력란 (OCR 결과 or 직접입력) - 남은 공간을 모두 차지 */}
        {inputStarted && !ocrMode && (
          <Box sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minHeight: 0, // flexbox에서 스크롤을 위해 필요
            overflow: 'hidden'
          }}>
            <FormattedTextEditor
              ref={textFieldRef}
              value={formattedHighlightText || highlightText}
              onChange={(formattedText) => {
                if (formattedText && formattedText.text) {
                  setHighlightText(formattedText.text);
                  setFormattedHighlightText(formattedText);
                } else if (typeof formattedText === 'string') {
                  setHighlightText(formattedText);
                  setFormattedHighlightText(null);
                } else {
                  setHighlightText('');
                  setFormattedHighlightText(null);
                }
                setInputStarted(true);
              }}
              placeholder="문장 내용을 입력하세요..."
              minRows={15}
              maxRows={40}
              disabled={isSaving}
              onSelectionChange={setHasSelection}
              disableFormatMenu={true}
            />
          </Box>
        )}
      </Box>

      {/* 하단 고정 플로팅 액션 바 - 키보드 위/화면 하단, 전체 컨테이너와 동일한 max-width */}
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: keyboardHeight,
          zIndex: 1200,
          maxWidth: '600px',
          margin: '0 auto',
          width: '100%',
          px: 2,
          py: 1,
          pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 44,
          gap: 1,
        }}
      >
        {/* 좌측: 서식/입력 관련 (B, 포맷 해제, 스캔) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={hasSelection ? "볼드 적용" : "텍스트 선택 후 볼드"}>
            <IconButton
              onPointerDown={(e) => {
                e.preventDefault();
                if (isSaving) return;
                if (textFieldRef.current?.applyBold) {
                  hasSelection ? textFieldRef.current.applyBold() : setSnackbar({ open: true, message: '볼드로 변경할 텍스트를 먼저 선택해주세요.', severity: 'info' });
                }
              }}
              disabled={isSaving}
              sx={{ minWidth: 44, minHeight: 44, color: hasSelection ? '#111' : '#999', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
            >
              <FormatBold sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={hasSelection ? "포맷 해제" : "텍스트 선택 후 포맷 해제"}>
            <IconButton
              onPointerDown={(e) => {
                e.preventDefault();
                if (isSaving) return;
                if (textFieldRef.current?.removeBold) {
                  hasSelection ? textFieldRef.current.removeBold() : setSnackbar({ open: true, message: '볼드 포맷팅을 제거할 텍스트를 먼저 선택해주세요.', severity: 'info' });
                }
              }}
              disabled={isSaving}
              sx={{ minWidth: 44, minHeight: 44, color: hasSelection ? '#111' : '#999', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
            >
              <FormatClear sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Button
            onClick={handleScanButtonClick}
            disabled={ocrLoading}
            startIcon={<CameraAltIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: 40,
              minWidth: 44,
              px: 1.5,
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: '#666',
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: '#111' },
            }}
          >
            스캔
          </Button>
        </Box>
        {/* 우측: 흐름 제어 (취소, 저장) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            onClick={handleCancelCurrentOrAll}
            disabled={isSaving}
            sx={{ minHeight: 40, minWidth: 44, px: 1.5, fontSize: '0.875rem', fontWeight: 500, color: '#666', textTransform: 'none', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveHighlight}
            disabled={isSaving || (!highlightText.trim() && !formattedHighlightText) || !bookInfo}
            sx={{
              minHeight: 40,
              minWidth: 44,
              px: 1.5,
              fontSize: '0.875rem',
              fontWeight: 600,
              bgcolor: '#111',
              color: '#fff',
              borderRadius: '8px',
              boxShadow: 'none',
              textTransform: 'none',
              '&:hover': { bgcolor: '#222', boxShadow: 'none' },
            }}
          >
            {isSaving ? <CircularProgress size={22} color="inherit" /> : '저장'}
          </Button>
        </Box>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/jpg"
          multiple
          style={{ display: 'none' }}
          onChange={handleImageSelect}
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration || 3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {showCropper && selectedImage && (
        <CropModal
          imageFile={selectedImage}
          onCropComplete={handleCropComplete}
          onClose={handleCropCancel}
        />
      )}

      {ocrLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1400
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <CircularProgress size={40} />
            <Typography>텍스트 추출 중...</Typography>
          </Paper>
        </Box>
      )}

      <Dialog open={scanConfirmOpen} onClose={handleScanConfirmNo} maxWidth="xs" fullWidth>
        <DialogTitle>이미지 스캔 중단 안내</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1, mb: 1.5 }}>
            새로 등록하면 저장된 내역이 모두 사라집니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleScanConfirmNo} color="inherit">취소</Button>
          <Button onClick={handleScanConfirmYes} variant="contained" sx={{ bgcolor: '#111', color: '#fff', '&:hover': { bgcolor: '#222', color: '#fff' } }}>계속하기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HighlightCreatePage; 